import React from 'react';
import MaterialTable, { MTableAction, MTableToolbar } from 'material-table';
import { createTheme, MuiThemeProvider } from '@material-ui/core/styles';
import GetAppIcon from '@material-ui/icons/GetApp';
import IconButton from '@material-ui/core/IconButton';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Divider from '@material-ui/core/Divider';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import RefreshIcon from '@material-ui/icons/Refresh';
// The bundled MaterialIcons-Regular.ttf is an old cut with 979 ligatures: it has no
// glyph for assignment_turned_in, assignment_ind, checkbox or insert_drive_file, so
// those row icons have been rendering blank. SVG components carry their own paths.
import EditIcon from '@material-ui/icons/Edit';
import EventIcon from '@material-ui/icons/Event';
import HowToRegIcon from '@material-ui/icons/HowToReg';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import MailIcon from '@material-ui/icons/Mail';
import DeleteIcon from '@material-ui/icons/Delete';

// Modals
import EhcParticipantForm from './ParticipantForm';
import EhcParticipantConfirmation from './ParticipantConfirmation';
import EhcParticipantsRemoval from './ParticipantsRemoval';
import { PARTICIPANT_CONFIRMED_STATUSES, PARTICIPANT_RESULT_STATUSES, PARTICIPANT_STATUSES, SERVICE_ORDER_STATUSES, FIT_STATUS_LABELS } from '../../constants/ehc/ParticipantConstants';
import EhcParticipantsCompletion from './ParticipantsCompletion';
import EhcParticipantCheckIn from './ParticipantCheckIn';
import AttachmentDataForm from '../AttachmentDataForm';
import AttachmentDataRemoval from '../AttachmentDataRemoval';
import { ATTACHABLES } from '../../constants/AttachmentConstants';
import { bigDownload } from '../../plugins/downloader';
import EhcParticipantsImport from './ParticipantsImport';
import EhcParticipantResultResend from './ParticipantResultResend';

const isParticipantCompletionLocked = (participant) =>
  participant?.status === PARTICIPANT_STATUSES.COMPLETE &&
  participant?.result_status === PARTICIPANT_RESULT_STATUSES.AVAILABLE;

// A walk-in arrives without a schedule, so check-in is offered from ordered onwards and the
// modal collects the hospital and service date when they are missing.
const canCheckIn = (participant) =>
  !!participant &&
  !participant.checked_in &&
  participant.status !== PARTICIPANT_STATUSES.COMPLETE &&
  participant.service_order_state === SERVICE_ORDER_STATUSES.IN_PROGRESS;

// Four enum values, three labels: ordered and booked are both pre-schedule states,
// so an admin reads them the same way. Mirrors Ehc::ServiceOrdersHelper#ehc_participant_status_badge.
const STATUS_BADGES = {
  [PARTICIPANT_STATUSES.ORDERED]: { key: 'unscheduled', tone: 'secondary' },
  [PARTICIPANT_STATUSES.BOOKED]: { key: 'unscheduled', tone: 'secondary' },
  [PARTICIPANT_STATUSES.CONFIRMED]: { key: 'scheduled', tone: 'info' },
  [PARTICIPANT_STATUSES.COMPLETE]: { key: 'completed', tone: 'success' }
};

const initialsOf = (name) => (name || '?')
  .trim()
  .split(/\s+/)
  .slice(0, 2)
  .map(part => part.charAt(0).toUpperCase())
  .join('');

// The chips filter the query server-side; filtering the loaded page would make the
// count on the chip disagree with the rows beneath it once the list is paginated.
const FILTER_CHIPS = [
  { value: null, key: 'all' },
  { value: 'unscheduled', key: 'unscheduled' },
  { value: 'scheduled', key: 'scheduled' },
  { value: 'examined', key: 'examined' },
  { value: 'checked_in', key: 'checked_in' },
  { value: 'results_available', key: 'results_available' }
];

// One labelled menu per row. The shifting icon set was unlearnable: which of the five
// icons appeared changed per row, so position carried no meaning.
class ParticipantRowActions extends React.PureComponent {
  state = { anchorEl: null };

  handleOpen = (event) => this.setState({ anchorEl: event.currentTarget });

  handleClose = () => this.setState({ anchorEl: null });

  handleSelect = (action) => () => {
    this.handleClose();
    action.onClick(null, this.props.rowData);
  };

  render() {
    const { actions, rowData } = this.props;
    const { anchorEl } = this.state;

    const visible = actions.filter(action =>
      !action.hidden && (!action.hideFunc || !action.hideFunc(rowData)));

    if (visible.length === 0) return null;

    return (
      <>
        <IconButton size="small" onClick={this.handleOpen} aria-label={this.props.moreLabel}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={this.handleClose} keepMounted>
          {visible.map((action, index) => [
            action.dividerBefore && <Divider key={`divider-${index}`} />,
            <MenuItem
              key={index}
              onClick={this.handleSelect(action)}
              disabled={action.disabled || (action.disableFunc && action.disableFunc(rowData))}
              className={action.destructive ? 'text-danger' : undefined}
            >
              <ListItemIcon style={{ minWidth: 34 }}>
                {action.menuIcon
                  ? React.createElement(action.menuIcon, { fontSize: 'small', color: action.destructive ? 'error' : 'inherit' })
                  : null}
              </ListItemIcon>
              <ListItemText primary={action.label || action.tooltip} />
            </MenuItem>
          ])}
        </Menu>
      </>
    );
  }
}


export default class EhcParticipantsDataTable extends React.Component {
  constructor(props) {
    super(props);
    this.tableRef = React.createRef();
    this.theme = createTheme({
      palette: {
        primary: {
          main: '#7cb342',
        },
        secondary: {
          main: '#4caf50',
        },
      },
    });

    this.downloadAttachmentLock = false; // Syncronous lock for handle function block.


    this.baseUrl = (company_id) => {
      if (!company_id) { // If company_id is not provided, use the one from props
        company_id = this.props.company_id;
      } else if (Array.isArray(company_id)) { // If company_id is an array, do not use company path
        company_id = null;
      }

      return `/api/ehc/${company_id ? `companies/${company_id}/` : ''}participants`;
    };

    this.formatISOToYMDHM = (isoDate) => {
      if (!isoDate) return '';
      const date = new Date(isoDate);

      const yyyy = date.getFullYear();
      const mm = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
      const dd = String(date.getDate()).padStart(2, '0');

      const hh = String(date.getHours()).padStart(2, '0');
      const min = String(date.getMinutes()).padStart(2, '0');

      return `${yyyy}-${mm}-${dd} ${hh}:${min}`;
    };

    // This function to try to get a nested field from an object
    // Usefull for custom render of Material Table that combined with grouping,
    // When grouping, custom render is used for displaying the data the param that passed is the field directly
    // It is assuming the fields of the data is not nested
    this.tryGetRowDataField = (input, field) => {
      if (input !== null && typeof input === "object") {
        return input[field];
      }
      return input;
    }

    // Default columns collapse to five plus the actions menu. The rest stay declared so
    // the existing column picker can still reach them - only `hidden` changes.
    const label = (key, fallback) => this.props.labels?.[key] || fallback;

    let baseColumns = [
      {
        title: label('column_participant', 'Peserta'), field: 'profile_full_name', orderField: 'profiles.full_name', grouping: false,
        render: rowData => {
          const name = this.tryGetRowDataField(rowData, 'profile_full_name');
          const nik = this.tryGetRowDataField(rowData, 'profile_nik');
          const email = this.tryGetRowDataField(rowData, 'profile_email');
          const subline = [nik, email].filter(Boolean).join(' · ');

          return (
            <div className="participants-table__identity">
              <span className="participants-table__avatar" aria-hidden="true">{initialsOf(name)}</span>
              <span className="participants-table__cell">
                <a href={this.tryGetRowDataField(rowData, 'attachments_url')}>{name}</a>
                {subline && <span className="participants-table__subline">{subline}</span>}
              </span>
            </div>
          );
        },
      },
      {
        title: label('column_status', 'Status'), field: 'human_status', orderField: 'status',
        render: rowData => {
          const status = this.tryGetRowDataField(rowData, 'status');
          const badge = STATUS_BADGES[status];
          if (!badge) return <span>{this.tryGetRowDataField(rowData, 'human_status') || '-'}</span>;

          return (
            <span className={`badge badge-${badge.tone}`}>
              {label(`status_${badge.key}`, this.tryGetRowDataField(rowData, 'human_status'))}
            </span>
          );
        },
      },
      {
        title: label('column_check_in', 'Check-in'), field: 'checked_in_at', grouping: false,
        render: rowData => {
          if (!this.tryGetRowDataField(rowData, 'checked_in')) {
            return <span className="text-muted">{label('not_checked_in', 'Belum check-in')}</span>;
          }

          const stamp = this.formatISOToYMDHM(this.tryGetRowDataField(rowData, 'checked_in_at'));
          const [date, time] = stamp ? stamp.split(' ') : [null, null];

          return (
            <span className="participants-table__cell">
              <span>{date || '-'}</span>
              {time && <span className="participants-table__subline">{time}</span>}
            </span>
          );
        },
      },
      {
        // Result status and fit status are one fact about one examination, so they
        // share a cell instead of two columns that are blank at the same times.
        title: label('column_result', 'Hasil pemeriksaan'), field: 'human_checkup_status', orderField: 'result_status',
        render: rowData => {
          const resultStatus = this.tryGetRowDataField(rowData, 'result_status');
          const fit = this.tryGetRowDataField(rowData, 'fit_status');
          const attachment = rowData.attachment?.file_url;

          if (resultStatus !== PARTICIPANT_RESULT_STATUSES.AVAILABLE) {
            return <span className="text-muted">{label('awaiting_result', 'Menunggu unggahan')}</span>;
          }

          return (
            <span className="participants-table__cell">
              <span className="text-success">
                {attachment
                  ? <a href={attachment} className="text-success" target="_blank" rel="noopener noreferrer">{label('result_available', 'Tersedia')}</a>
                  : label('result_available', 'Tersedia')}
              </span>
              {fit && <span className="participants-table__subline">{FIT_STATUS_LABELS[fit] || fit}</span>}
            </span>
          );
        },
      },
      { title: label('column_package', 'Paket'), field: 'service_package_name', orderField: 'support_services.name_id' },

      // Available through the column picker, hidden by default.
      { title: 'Avatar', field: 'profile_avatar_url', export: false, width: 20, grouping: false, sorting: false, hidden: true,
        render: rowData => (
          <a href={this.tryGetRowDataField(rowData, 'attachments_url')}>
            <img
              style={{ height: 36, borderRadius: '50%' }}
              src={rowData.profile_avatar_url}
            />
          </a>
        ),
      },
      { title: 'Check In', field: 'checked_in', grouping: false, sorting: false, hidden: true,
        render: rowData => (<span>{rowData.checked_in ? '✓' : '-'}</span>),
      },
      { title: 'Fit Status', field: 'fit_status', orderField: 'fit_status', hidden: true,
        render: rowData => {
          const value = this.tryGetRowDataField(rowData, 'fit_status');
          return <span>{value ? (FIT_STATUS_LABELS[value] || value) : '-'}</span>;
        },
      },
      { title: 'Result Attachment', field: 'attachment', grouping: false, sorting: false, hidden: true,
        render: rowData => (<>
          {rowData.attachment?.file_url && <a href={rowData.attachment.file_url} className="text-success" target="_blank" rel="noopener noreferrer"><GetAppIcon fontSize="large" /></a>}
        </>),
      },
      { title: 'Email', field: 'profile_email', orderField: 'profiles.email', grouping: false, hidden: true },
      { title: 'NIK', field: 'profile_nik', orderField: 'profiles.nik', grouping: false, hidden: true },
      { title: 'DOB', field: 'profile_date_of_birth', orderField: 'profiles.date_of_birth', grouping: false, hidden: true },
      { title: 'Gender', field: 'profile_gender', orderField: 'profiles.gender', hidden: true },
      { title: 'Phone', field: 'profile_mobile_phone', orderField: 'profiles.mobile_phone', grouping: false, hidden: true },
      {
        title: 'Company', field: 'company_name', orderField: 'companies.name', hidden: true,
        render: rowData => (
          <a href={this.tryGetRowDataField(rowData, 'company_url')}>
            {this.tryGetRowDataField(rowData, 'company_name')}
          </a>
        ),
      },
      { title: 'ID', field: 'employment_id_number', grouping: false, hidden: true },
      { title: 'Batch Code', field: 'order_name', orderField: 'ehc_service_orders.order_name', grouping: false, hidden: true },
      { title: 'Batch Status', field: 'service_order_status', orderField: 'ehc_service_orders.status', hidden: true },
      { title: 'Covered', field: 'covered', hidden: true },
      { title: 'Show Result', field: 'result_enabled', hidden: true },
      { title: 'Hospital', field: 'hospital_name', orderField: 'hospitals.name', hidden: true },
      { title: 'Service Date', field: 'visit_date', grouping: false, hidden: true },
      { title: 'Additional Tests', field: 'applied_additions_count', grouping: false, sorting: false, hidden: true },
      {
        title: 'Confirmed Doctors', field: 'order_doctors', grouping: false, sorting: false, hidden: true,
        render: rowData => (<span>{Object.values(this.tryGetRowDataField(rowData, 'order_doctors') || {}).join(', ')}</span>),
      },
      {
        title: 'Confirmed Time', field: 'confirmed_datetime', grouping: false, hidden: true,
        render: rowData => (<span>{this.formatISOToYMDHM(this.tryGetRowDataField(rowData, 'confirmed_datetime'))}</span>),
       },
      { title: 'Notes', field: 'notes', grouping: false, sorting: false, hidden: true },
      { title: 'Last Sign', field: 'current_sign_in_at', orderField: 'accounts.current_sign_in_at', searchable: false, grouping: false, hidden: true },
      { title: 'Register At', field: 'created_at', orderField: 'profiles.created_at', searchable: false, grouping: false, hidden: true },
    ]

    // Arrange action
    // Toolbar icons are SVG components for the same reason as the row menu: the bundled
    // icon font has no glyph for file_download, so the export button rendered blank.
    let baseActions = [
      {
        icon: () => <RefreshIcon />,
        tooltip: 'Refresh Data',
        isFreeAction: true,
        onClick: () =>
          this.tableRef.current && this.tableRef.current.onQueryChange(),
      },
      {
        icon: () => <GetAppIcon />,
        tooltip: 'Download All Checkup Results',
        isFreeAction: true,
        onClick: () => this.downloadAttachments(),
      },
    ]

    // When editable
    // A retrospective order finalises its participants at submit time, so the
    // operational schedule / check-in / complete steps do not apply to it.
    // Every predicate below is the one that already gated the icon it replaces.
    const isRetrospective = this.props.service_order?.entry_mode === 'retrospective';
    if (this.props.editable) {
      baseActions.push({
        icon: 'edit',
        menuIcon: EditIcon,
        label: label('action_edit', 'Ubah data peserta'),
        tooltip: 'Edit',
        position: 'row',
        onClick: (evt, selectedRow) => this.handleOpenParticipantEditModal(selectedRow)
      });
      if (!isRetrospective) {
        baseActions.push({
          icon: 'event',
          menuIcon: EventIcon,
          label: label('action_reschedule', 'Ubah jadwal'),
          tooltip: 'Update Schedule',
          position: 'row',
          onClick: (evt, selectedRow) => this.handleOpenParticipantConfirmationModal(selectedRow),
          disableFunc: (rowData) => ((rowData.status !== PARTICIPANT_STATUSES.BOOKED && rowData.status !== PARTICIPANT_STATUSES.CONFIRMED) || rowData.checked_in),
          hideFunc: (rowData) => ((rowData.status !== PARTICIPANT_STATUSES.BOOKED && rowData.status !== PARTICIPANT_STATUSES.CONFIRMED) || rowData.checked_in)
        });
        baseActions.push({
          icon: 'how_to_reg',
          menuIcon: HowToRegIcon,
          label: label('action_check_in', 'Check-in peserta'),
          tooltip: 'Check In',
          position: 'row',
          onClick: (evt, selectedRow) => this.handleOpenParticipantCheckInModal(selectedRow),
          disableFunc: (rowData) => !canCheckIn(rowData),
          hideFunc: (rowData) => !canCheckIn(rowData)
        });
        baseActions.push({
          icon: 'check_box',
          menuIcon: CheckBoxIcon,
          label: label('action_complete', 'Tandai selesai diperiksa'),
          tooltip: 'Complete',
          position: 'row',
          onClick: (evt, selectedRow) => this.handleOpenParticipantsCompletionModal([selectedRow]),
          disableFunc: (rowData) => (!rowData.checked_in || isParticipantCompletionLocked(rowData)),
          hideFunc: (rowData) => (!rowData.checked_in || isParticipantCompletionLocked(rowData))
        });
      }
      baseActions.push({
        icon: 'insert_drive_file',
        menuIcon: InsertDriveFileIcon,
        label: label('action_upload_result', 'Unggah hasil'),
        tooltip: 'Upload Result',
        position: 'row',
        onClick: (evt, selectedRow) => this.handleOpenParticipantAttachmentModal(selectedRow),
        disableFunc: (rowData) => (rowData.status !== PARTICIPANT_STATUSES.COMPLETE),
        hideFunc: (rowData) => (rowData.status !== PARTICIPANT_STATUSES.COMPLETE),
      });
      baseActions.push({
        icon: 'mail',
        menuIcon: MailIcon,
        label: label('action_resend_result', 'Kirim ulang email hasil'),
        tooltip: 'Resend Result Email',
        position: 'row',
        onClick: (evt, selectedRow) => this.handleOpenResultResendModal(selectedRow),
        hideFunc: (rowData) => !isParticipantCompletionLocked(rowData)
      });
    }

    // When removable
    if (this.props.removable) {
      baseActions.push({
        icon: 'delete',
        menuIcon: DeleteIcon,
        label: label('action_remove', 'Hapus peserta'),
        tooltip: 'Remove',
        position: 'row',
        destructive: true,
        dividerBefore: true,
        onClick: (evt, selectedRow) => this.handleOpenParticipantRemovalModal([selectedRow]),
        disableFunc: (rowData) => isParticipantCompletionLocked(rowData),
        hideFunc: (rowData) => rowData.service_order_state !== SERVICE_ORDER_STATUSES.DRAFT
      });
      baseActions.push({
        icon: 'delete',
        tooltip: 'Batch Remove',
        onClick: (evt, selectedRows) => this.handleOpenParticipantRemovalModal(selectedRows)
      });
    }

    this.state = {
      serviceOrder: this.props.service_order || null,

      columns: baseColumns,
      actions: baseActions,
      data: [],
      page: null,
      open: false,
      totalCount: null,

      // Toolbar chip; drives the server-side query, not the loaded page.
      filter: null,
      filterCounts: null,
      // Action state
      isDownloadingAttachment: false, // state lock for update rendering

      // Modal state
      participantEditModalOpen: false,
      participantCreateModalOpen: false,
      participantConfirmationModalOpen: false,
      participantsCompletionModalOpen: false,
      participantCheckInModalOpen: false,
      participantsRemovalModalOpen: false,
      participantAttachmentFormModalOpen: false,
      participantAttachmentRemovalModalOpen: false,
      participantsImportModalOpen: false,
      participantResultResendModalOpen: false,
      selectedParticipants: null,
      editedParticipant: null,
      editedAttachment: null,
      selectedProfiles: null
    };
  }

  // =====================
  // Modal Handlers
  // =====================
  handleOpenParticipantEditModal = async (participant) => {
    this.setState({
      participantEditModalOpen: true,
      participantConfirmationModalOpen: false,
      participantsCompletionModalOpen: false,
      participantsRemovalModalOpen: false,
      participantAttachmentFormModalOpen: false,
      participantAttachmentRemovalModalOpen: false,
      selectedParticipants: null,
      editedParticipant: participant,
      editedAttachment: null,
      selectedProfiles: null
    });
  };

  handleOpenParticipantRemovalModal = (participants) => {
    this.setState({
      participantEditModalOpen: false,
      participantConfirmationModalOpen: false,
      participantsCompletionModalOpen: false,
      participantsRemovalModalOpen: true,
      participantAttachmentFormModalOpen: false,
      participantAttachmentRemovalModalOpen: false,
      selectedParticipants: participants,
      editedParticipant: null,
      editedAttachment: null,
      selectedProfiles: null
    });
  };

  handleOpenParticipantConfirmationModal = async (participant) => {
    if (!participant || (participant.status !== PARTICIPANT_STATUSES.BOOKED && participant.status !== PARTICIPANT_STATUSES.CONFIRMED) || participant.checked_in) return;

    this.setState({
      participantEditModalOpen: false,
      participantConfirmationModalOpen: true,
      participantsCompletionModalOpen: false,
      participantsRemovalModalOpen: false,
      participantAttachmentFormModalOpen: false,
      participantAttachmentRemovalModalOpen: false,
      selectedParticipants: null,
      editedParticipant: participant,
      editedAttachment: null,
      selectedProfiles: null
    });
  };

  handleOpenParticipantsCompletionModal = async (participants) => {
    if (!participants || participants.length === 0 || participants.find(e => !e.checked_in || isParticipantCompletionLocked(e))) return;

    this.setState({
      participantEditModalOpen: false,
      participantConfirmationModalOpen: false,
      participantsCompletionModalOpen: true,
      participantsRemovalModalOpen: false,
      participantAttachmentFormModalOpen: false,
      participantAttachmentRemovalModalOpen: false,
      selectedParticipants: participants,
      editedParticipant: null,
      editedAttachment: null,
      selectedProfiles: null
    });
  };

  handleOpenParticipantCheckInModal = (participant) => {
    if (!canCheckIn(participant)) return;

    this.setState({
      participantEditModalOpen: false,
      participantConfirmationModalOpen: false,
      participantsCompletionModalOpen: false,
      participantCheckInModalOpen: true,
      participantsRemovalModalOpen: false,
      participantAttachmentFormModalOpen: false,
      participantAttachmentRemovalModalOpen: false,
      selectedParticipants: [participant],
      editedParticipant: null,
      editedAttachment: null,
      selectedProfiles: null
    });
  };

  handleOpenParticipantAttachmentModal = async (participant) => {
    if (!participant || participant.status !== PARTICIPANT_STATUSES.COMPLETE) return;

    const profile = [participant.profile_full_name, participant.profile_id];
    let attachment;

    // Both new upload and edit go through the single upload_result endpoint
    // (POST creates a result file, PUT edits fit_status / replaces the file).
    const uploadResultUrl = `${this.baseUrl(participant.company_id)}/${participant.id}/upload_result`;

    if (participant.attachment?.id > 0) {
      attachment = { ...participant.attachment, fit_status: participant.fit_status };
    } else {
      attachment = {
        profile_id: participant.profile_id,
        hospital_id: participant.hospital_id,
        doctor_id: participant.doctor_ids?.length > 0 ? participant.doctor_ids[0] : null,
        attachable_type: ATTACHABLES.EHC_SERVICE_ORDER,
        attachable_id: participant.service_order_id,
        fit_status: participant.fit_status
      };
    }

    this.setState({
      participantEditModalOpen: false,
      participantConfirmationModalOpen: false,
      participantsCompletionModalOpen: false,
      participantsRemovalModalOpen: false,
      participantAttachmentFormModalOpen: true,
      participantAttachmentRemovalModalOpen: false,
      selectedParticipants: null,
      editedParticipant: participant,
      editedAttachment: attachment,
      selectedProfiles: [profile],
      uploadResultUrl
    });
  };

  handleOpenAttachmentRemovalModal = async (participant) => {
    if (!participant || participant.status !== PARTICIPANT_STATUSES.COMPLETE || !participant.attachment?.id) return;

    this.setState({
      participantEditModalOpen: false,
      participantConfirmationModalOpen: false,
      participantsCompletionModalOpen: false,
      participantsRemovalModalOpen: false,
      participantAttachmentFormModalOpen: false,
      participantAttachmentRemovalModalOpen: true,
      selectedParticipants: null,
      editedParticipant: participant,
      editedAttachment: participant.attachment,
      selectedProfiles: null
    });
  };

  handleOpenResultResendModal = (participant) => {
    if (!participant || participant.status !== PARTICIPANT_STATUSES.COMPLETE
        || participant.result_status !== PARTICIPANT_RESULT_STATUSES.AVAILABLE) return;

    this.setState({
      participantEditModalOpen: false,
      participantConfirmationModalOpen: false,
      participantsCompletionModalOpen: false,
      participantsRemovalModalOpen: false,
      participantAttachmentFormModalOpen: false,
      participantAttachmentRemovalModalOpen: false,
      participantResultResendModalOpen: true,
      selectedParticipants: null,
      editedParticipant: participant,
      editedAttachment: null,
      selectedProfiles: null
    });
  };

  handleCloseModal = () => {
    this.setState({
      participantEditModalOpen: false,
      participantCreateModalOpen: false,
      participantConfirmationModalOpen: false,
      participantsCompletionModalOpen: false,
      participantCheckInModalOpen: false,
      participantsRemovalModalOpen: false,
      participantAttachmentFormModalOpen: false,
      participantAttachmentRemovalModalOpen: false,
      participantsImportModalOpen: false,
      participantResultResendModalOpen: false,
      selectedParticipants: null,
      editedParticipant: null,
      editedAttachment: null,
      selectedProfiles: null,
      uploadResultUrl: null
    });
  };

  handleOpenCreateModal = () => {
    this.setState({
      participantEditModalOpen: false,
      participantCreateModalOpen: true,
      participantConfirmationModalOpen: false,
      participantsCompletionModalOpen: false,
      participantCheckInModalOpen: false,
      participantsRemovalModalOpen: false,
      participantAttachmentFormModalOpen: false,
      participantAttachmentRemovalModalOpen: false,
      participantsImportModalOpen: false,
      participantResultResendModalOpen: false,
      selectedParticipants: null,
      editedParticipant: null,
      editedAttachment: null,
      selectedProfiles: null,
      uploadResultUrl: null
    });
  };

  handleOpenImportModal = () => {
    this.setState({ participantsImportModalOpen: true });
  };

  handleImportSuccess = () => {
    if (this.tableRef.current) this.tableRef.current.onQueryChange();
  };

  // =====================
  // MaterialTable Queries
  // =====================
  getApiErrorMessage(result) {
    if (result.success) {
      return null;
    }
    if (result.errors && typeof result.errors === 'object' && !Array.isArray(result.errors)) {
      // handle validation error hash
      return 'Validation: ' + Object.entries(result.errors).map(([key, value]) => `${key}: ${value}`).join(', ');
    }
    if (result.errors && typeof result.errors === 'string') {
      return result.errors;
    }
    if (Array.isArray(result.errors) && result.errors.length > 0) {
      return result.errors.join(', ');
    }
    console.error('Error fetching data:', result);
    if (result.message) {
      return result.message;
    }
    return 'There is something wrong';
  }

  async downloadAttachments() {
    if (!this.props.service_order_id) return;

    await bigDownload(
      `/ehc/service-orders/${this.props.service_order_id}/download_attachments`,
      () => {
        if (this.downloadAttachmentLock) return false;

        this.downloadAttachmentLock = true;
        this.setState({ isDownloadingAttachment: true });
        return true;
      },
      () => {
        this.downloadAttachmentLock = false;
        this.setState({ isDownloadingAttachment: false });
        return true;
      }
    );
  }

  fetchData(query) {
    return new Promise((resolve, reject) => {
      const requestedPage = query.page + 1;

      console.log('Query details:', {
        page: query.page,
        pageSize: query.pageSize,
        requestedPage: requestedPage,
        search: query.search
      });

      const baseParams = 'per_page=' + query.pageSize
        + (this.props.service_order_id ? '&service_order_id=' + this.props.service_order_id : '')
        + (this.state.filter ? '&filter=' + this.state.filter : '')
        + '&q=' + query.search
        + '&sort_by=' + (query.orderBy?.orderField ?? query.orderBy?.field ?? '')
        + '&sort_order=' + (query.orderDirection ?? 'asc');
      const url = this.baseUrl() + '?' + baseParams + '&page=' + requestedPage;

      fetch(url)
        .then(async res => {
          if (res && res.status === 200 && res.ok) {
            return res.json();
          } else {
            console.log('Error response, fetching fallback data');
            const lastPageUrl = this.baseUrl() + '?' + baseParams + '&page=1';
            const fallbackRes = await fetch(lastPageUrl);
            const fallbackData = await fallbackRes.json();
            const lastPage = fallbackData.pagination.last;

            console.log('Fallback - redirecting to last page:', lastPage);
            const correctedUrl = this.baseUrl() + '?' + baseParams + '&page=' + lastPage;
            const correctedRes = await fetch(correctedUrl);
            return await correctedRes.json();
          }
        })
        .then(data => {
          console.log('Fetch response:', data);

          const currentPage = data.pagination.page - 1;
          const totalCount = data.pagination.count;
          const actualPageSize = parseInt(data.pagination.vars.items) || query.pageSize;
          const lastPage = data.pagination.last - 1;

          console.log('Pagination calculation:', {
            currentPage,
            totalCount,
            actualPageSize,
            lastPage,
            requestedPage: query.page,
            queryPageSize: query.pageSize
          });

          let adjustedTotalCount = totalCount;
          if (query.page > lastPage) {

            adjustedTotalCount = (lastPage + 1) * query.pageSize;
            console.log('Adjusted total count to prevent overflow:', adjustedTotalCount);
          }

          // Chip counts describe the whole order, so they ride along with each page
          // rather than being derived from the rows just returned.
          if (data.filter_counts) this.setState({ filterCounts: data.filter_counts });

          resolve({
            data: data.participants,
            page: currentPage,
            totalCount: adjustedTotalCount
          });
        })
        .catch(error => {
          console.error('Fetch error:', error);
          reject(error);
        });
    });
  }

  // =====================
  // Render Modal
  // =====================
  renderModals() {
    return <>
      {(this.props.editable || this.props.removable) &&
        <EhcParticipantForm
          mode="edit"
          participant={this.state.editedParticipant}
          hospitals={this.props.hospitals}
          editable={this.props.editable}
          removable={this.props.removable}
          addTitle={this.props.labels?.add_title}
          editTitle={this.props.labels?.edit_title}
          labels={this.props.labels}
          tableRef={this.tableRef}
          open={this.state.participantEditModalOpen}
          onClose={this.handleCloseModal}
          onRemove={() => this.handleOpenParticipantRemovalModal([this.state.editedParticipant])}
        ></EhcParticipantForm>
      }

      {this.state.participantCreateModalOpen &&
        <EhcParticipantForm
          mode="create"
          participant={{
            company_id: this.props.company_id,
            covered: true,
            result_enabled: true,
            applied_additions: {},
            service_order: this.props.service_order
          }}
          companyId={this.props.company_id}
          serviceOrderId={this.props.service_order_id}
          serviceOrder={this.props.service_order}
          hospitals={this.props.hospitals}
          editable={true}
          removable={false}
          addTitle={this.props.labels?.add_title}
          editTitle={this.props.labels?.edit_title}
          labels={this.props.labels}
          tableRef={this.tableRef}
          open={this.state.participantCreateModalOpen}
          onClose={this.handleCloseModal}
        />
      }

      {this.props.editable &&
        <EhcParticipantConfirmation
          serviceOrder={this.state.serviceOrder}
          participant={this.state.editedParticipant}
          labels={this.props.labels}
          editable={this.props.editable}
          tableRef={this.tableRef}
          open={this.state.participantConfirmationModalOpen}
          onClose={this.handleCloseModal}
        ></EhcParticipantConfirmation>
      }

      {this.props.removable &&
        <EhcParticipantsRemoval
          participants={this.state.selectedParticipants}
          removable={this.props.removable}
          tableRef={this.tableRef}
          open={this.state.participantsRemovalModalOpen}
          onClose={this.handleCloseModal}
        ></EhcParticipantsRemoval>
      }

      {this.props.editable &&
        <EhcParticipantsCompletion
          participants={this.state.selectedParticipants}
          editable={this.props.editable}
          tableRef={this.tableRef}
          open={this.state.participantsCompletionModalOpen}
          onClose={this.handleCloseModal}
        ></EhcParticipantsCompletion>
      }

      {this.props.editable &&
        <EhcParticipantCheckIn
          participants={this.state.selectedParticipants}
          serviceOrder={this.state.serviceOrder}
          hospitals={this.props.hospitals}
          lockedHospitalId={this.props.branch_hospital_id}
          labels={this.props.labels}
          editable={this.props.editable}
          tableRef={this.tableRef}
          open={this.state.participantCheckInModalOpen}
          onClose={this.handleCloseModal}
        ></EhcParticipantCheckIn>
      }

      {this.props.editable &&
        <AttachmentDataForm
          fromAttachable={true}
          hospitals={this.props.hospitals}
          profiles={this.state.selectedProfiles}
          attachment={this.state.editedAttachment}
          removable={this.props.removable}
          editable={this.props.editable}
          tableRef={this.tableRef}
          open={this.state.participantAttachmentFormModalOpen}
          onClose={this.handleCloseModal}
          onRemove={() => this.handleOpenAttachmentRemovalModal(this.state.editedParticipant)}
          uploadUrl={this.state.uploadResultUrl}
          accept=".pdf"
        ></AttachmentDataForm>
      }

      {this.props.removable &&
        <AttachmentDataRemoval
          attachments={this.state.editedAttachment ? [this.state.editedAttachment] : null}
          removable={this.props.removable}
          tableRef={this.tableRef}
          open={this.state.participantAttachmentRemovalModalOpen}
          onClose={this.handleCloseModal}
        ></AttachmentDataRemoval>
      }

      {this.props.editable &&
        <EhcParticipantResultResend
          participant={this.state.editedParticipant}
          tableRef={this.tableRef}
          open={this.state.participantResultResendModalOpen}
          onClose={this.handleCloseModal}
        />
      }

      {this.props.editable && this.props.service_order_id && this.state.participantsImportModalOpen &&
        <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{this.props.labels?.import_action || 'Import Peserta'} dari Excel</h5>
                <button type="button" className="close" onClick={this.handleCloseModal}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <EhcParticipantsImport
                  importUrl={`${this.baseUrl(this.props.company_id)}/import`}
                  importStatusUrl={`${this.baseUrl(this.props.company_id)}/import_status`}
                  importCancelUrl={`${this.baseUrl(this.props.company_id)}/import_cancel`}
                  importActiveUrl={`${this.baseUrl(this.props.company_id)}/import_active`}
                  templateUrl={`${this.baseUrl(this.props.company_id)}/download_template?service_order_id=${this.props.service_order_id}`}
                  serviceOrderId={this.props.service_order_id}
                  onClose={this.handleCloseModal}
                  onSuccess={this.handleImportSuccess}
                  labels={this.props.labels}
                />
              </div>
            </div>
          </div>
        </div>
      }
    </>
  }

  // =====================
  // Toolbar filter chips
  // =====================
  handleFilterChange = (value) => () => {
    if (this.state.filter === value) return;

    this.setState({ filter: value }, () => {
      if (this.tableRef.current) this.tableRef.current.onQueryChange({ page: 0 });
    });
  };

  renderFilterChips = () => {
    const counts = this.state.filterCounts;

    return (
      <div className="participants-table__chips" role="group">
        {FILTER_CHIPS.map(chip => {
          const count = counts ? counts[chip.key] : null;

          return (
            <button
              key={chip.key}
              type="button"
              className={`participants-table__chip${this.state.filter === chip.value ? ' participants-table__chip--active' : ''}`}
              onClick={this.handleFilterChange(chip.value)}
              aria-pressed={this.state.filter === chip.value}
            >
              {this.props.labels?.[`filter_${chip.key}`] || chip.key}
              {count !== null && count !== undefined &&
                <span className="participants-table__chip-count">{count}</span>
              }
            </button>
          );
        })}
      </div>
    );
  };

  // =====================
  // Render Toolbar (class property — stable ref, prevents search focus loss)
  // =====================
  renderToolbar = (props) => (
    <div>
      {this.renderFilterChips()}
      <div style={{ display: 'flex', alignItems: 'center', paddingRight: 8 }}>
        <div style={{ flex: 1 }}>
          <MTableToolbar
            {...props}
            searchFieldAlignment="right"
            toolbarButtonAlignment="right"
          />
        </div>
      {this.props.editable && this.props.service_order_id && (
        <div style={{ flexShrink: 0, paddingLeft: 8 }}>
          <button
            type="button"
            className="btn btn-primary btn-sm mr-2"
            onClick={this.handleOpenCreateModal}
          >
            <i className="zmdi zmdi-plus mr-1" />
            {this.props.labels?.add || 'Tambah Peserta'}
          </button>
          <button
            type="button"
            className="btn btn-outline-primary btn-sm"
            onClick={this.handleOpenImportModal}
          >
            <i className="zmdi zmdi-upload mr-1" />
            {this.props.labels?.import_action || 'Import Peserta'}
          </button>
        </div>
      )}
      {this.props.export_urls && (
        <div style={{ flexShrink: 0, paddingLeft: 8 }}>
          <div className="dropdown">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm dropdown-toggle"
              data-toggle="dropdown"
              aria-haspopup="true"
              aria-expanded="false"
            >
              <i className="zmdi zmdi-download mr-1" />
              {this.props.labels?.export || 'Export'}
            </button>
            <div className="dropdown-menu dropdown-menu-right">
              <a className="dropdown-item" href={this.props.export_urls.csv}>
                {this.props.labels?.export_csv || 'CSV (.csv)'}
              </a>
              <a className="dropdown-item" href={this.props.export_urls.xlsx}>
                {this.props.labels?.export_excel || 'Excel (.xlsx)'}
              </a>
            </div>
          </div>
        </div>
      )}
      {this.props.submit_url && (
        <div style={{ flexShrink: 0, paddingLeft: 8 }}>
          {this.state.data && this.state.data.length > 0 ? (
            <a
              href={this.props.submit_url}
              data-method="post"
              data-confirm={this.props.submit_confirm}
              rel="nofollow"
              className="btn btn-success btn-sm"
            >
              <i className="zmdi zmdi-mail-send mr-1" />
              {this.props.submit_label}
            </a>
          ) : (
            <button type="button" className="btn btn-secondary btn-sm" disabled title={this.props.submit_disabled_title}>
              <i className="zmdi zmdi-mail-send mr-1" />
              {this.props.submit_label}
            </button>
          )}
        </div>
      )}
      {this.props.revert_url && (
        <div style={{ flexShrink: 0, paddingLeft: 8 }}>
          <a
            href={this.props.revert_url}
            data-method="post"
            data-confirm={this.props.revert_confirm}
            rel="nofollow"
            className="btn btn-secondary btn-sm"
          >
            <i className="zmdi zmdi-arrow-left mr-1" />
            {this.props.revert_label}
          </a>
        </div>
      )}
      </div>
    </div>
  );

  // =====================
  // Render Component
  // =====================
  render() {
    return (
      <MuiThemeProvider theme={this.theme}>

        {this.renderModals()}

        <div className="participants-table-wrapper">
        <MaterialTable
          title={this.props.title + (this.state.isDownloadingAttachment ? ' (Downloading attachments...)' : '')}
          columns={this.state.columns}
          tableRef={this.tableRef}
          actions={this.state.actions}
          localization={{
            // The design gives the actions column no heading — just the kebab per row.
            header: { actions: '' }
          }}
          options={{
            pageSize: 20,
            pageSizeOptions: [20, 30, 50],
            exportButton: false,
            sorting: true,
            search: true,
            // The "Drag headers here to group by" strip was permanent noise for every
            // user. Columns keep their grouping flags so it can be re-enabled here.
            grouping: false,
            columnsButton: true,
            selection: this.props.removable,
            // Actions belong at the end of the row, as on every other table in this app.
            actionsColumnIndex: -1,
            addRowPosition: 'first',
            // Force MaterialTable to respect the pagination boundaries
            emptyRowsWhenPaging: false,
            // This will help ensure proper pagination behavior
            paginationType: 'normal'
          }}
          components={{
            Toolbar: this.renderToolbar,
            Actions: props => {
              // Toolbar actions keep the plain button row; row actions collapse into
              // one labelled menu so the visible set stops shifting per row.
              if (props.data === undefined || Array.isArray(props.data)) {
                return (<>
                  {props.actions && props.actions.length > 0 &&
                    props.actions
                    .filter(action => !action.hidden && action.position !== 'row')
                    .map((action, index) =>
                      <MTableAction
                        key={index}
                        action={action}
                        data={props.data}
                        size={props.size}
                        disabled={props.disabled || action.disabled}
                      />
                  )}
                </>);
              }

              const rowActions = (props.actions || []).filter(action =>
                typeof action !== 'function' && action.position === 'row');

              return (
                <ParticipantRowActions
                  actions={rowActions}
                  rowData={props.data}
                  moreLabel={this.props.labels?.row_actions || 'Aksi'}
                />
              );
            }
          }}
          data={query =>
            this.fetchData(query).then(result => {
              // Update component state to track current pagination
              this.setState({
                data: result.data,
                currentPage: result.page,
                totalCount: result.totalCount
              });
              return result;
            })
          }
        />
        </div>
        <span style={{ color: 'red' }} hidden={!this.state.error}>* { this.state.error }</span>
      </MuiThemeProvider>
    );
  }
}
